import {BehaviorSubject, combineLatest, interval, of, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, mapTo, shareReplay, skip, startWith, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {mc} from '@/core';
import {web3Api} from '@/api/web3';
import {userStatus$, isLogin$} from '@/streams/auth';
import {userInfo$} from '@/streams/user';
import {invalidWallets$, wallets$} from '@/streams/assets-spot';
import {genShortWalletAddress} from './utils/util';

export const mintStatusMap = {
  unbindAddress: 0,
  bindAddress: 1,
  minting: 2,
  minted: 3,
  revoking: 4,
};

export const addressSourceMap = {
  phemex: 'Phemex',
  userInput: 'UserInput',
  metaMask: 'MetaMask',
  walletConnect: 'WalletConnect',
};

const defaultUserWeb3WalletInfo = {address: '', status: mintStatusMap.unbindAddress};
const defaultSoulPassInfo = {
  description: '',
  externalUrl: '',
  imageUrl: '',
  mintTime: 0,
  name: '',
  passId: '',
};
const defaultMintQualifyStatus = {
  qualified: false, //是否准入
  whitelisted: false, //是否是白名单
  tradingVolumeMet: false, //是否交易量达标
  minTradingVol: '',
  userTradingVol: '',
  ptBalMet: false, // PT持仓量是否满足要求
  userPtBal: '0', // 用户当前的PT持仓量(可用余额，已扣除冻结中部分，包括Convert或者出金)
  minPtBal: '500', // PT持仓量最低要求
};

export const userWeb3WalletInfo$ = new BehaviorSubject(defaultUserWeb3WalletInfo);
export const soulPassInfo$ = new BehaviorSubject(defaultSoulPassInfo);
export const loadSoulPassInfo$ = new Subject();
export const isInitUserWeb3WalletInfo$ = new BehaviorSubject(true);
export const loadUserWeb3WalletInfo$ = new Subject();
export const isMinted$ = userWeb3WalletInfo$.pipe(
  skip(1),
  map(({status}) => [mintStatusMap.minted, mintStatusMap.revoking].includes(status))
);

export const isInitWeb3ModuleState$ = new BehaviorSubject(true);
export const web3ModuleState$ = combineLatest([userStatus$, userInfo$]).pipe(
  tap(([status]) => {
    if (status !== 'Init') {
      isInitWeb3ModuleState$.next(false);
    }
  }),
  map(([, userInfo]) => !userInfo.isSub),
  distinctUntilChanged(),
  startWith(true),
  shareReplay(1)
);

export const mintQualifyPopupVisibleItem$ = new BehaviorSubject(null);
export const loadMintQualifyStatus$ = new Subject();
export const mintQualifyStatusLoading$ = new BehaviorSubject(false);
// 完成Basic KYC或Advance KYC + 过去30天，合约+现货交易量10万U（取实时的过去30天30天合约+现货交易量之和，前29天+当日实时）
export const mintQualifyStatus$ = new BehaviorSubject(defaultMintQualifyStatus);
export const phemexEquityWallet$ = new BehaviorSubject({
  time: 0,
  name: '',
  currency: '',
  totalBalanceEv: 0,
  totalBalance: '0',
  availableBalanceEv: 0,
  availableBalance: '0',
  freezeBalanceEv: 0,
  freezeBalance: '0',
  lockedTradingBalanceEv: 0,
  lockedTradingBalance: '0',
  lockedWithdrawEv: 0,
  lockedWithdraw: '0',
});

loadUserWeb3WalletInfo$.pipe(switchMap(getSoulPassStatus), filter(Boolean)).subscribe((o) => {
  userWeb3WalletInfo$.next({
    ...o,
    shortAddress: genShortWalletAddress(o.address),
  });
  isInitUserWeb3WalletInfo$.next(false);
});

loadSoulPassInfo$.pipe(switchMap(getSoulPassOverview), filter(Boolean)).subscribe((o) => soulPassInfo$.next(o));

loadMintQualifyStatus$
  .pipe(
    withLatestFrom(mintQualifyStatus$),
    filter(([, mintQualifyStatus]) => !mintQualifyStatus.qualified),
    tap(() => mintQualifyStatusLoading$.next(true)),
    switchMap(() => web3Api.getMintQualify())
  )
  .subscribe(({data, error}) => {
    mintQualifyStatusLoading$.next(false);
    mintQualifyStatus$.next(data || defaultMintQualifyStatus);
    if (error) {
      mc.send('toast', 'error', error);
    }
  });

combineLatest([isLogin$, userInfo$])
  .pipe(
    map(([isLogin, userInfo]) => isLogin && userInfo.isMain),
    filter(Boolean),
    take(1)
  )
  .subscribe(() => {
    loadMintQualifyStatus$.next();
    loadUserWeb3WalletInfo$.next();
  });

userWeb3WalletInfo$
  .pipe(
    map((o) => o.status),
    distinctUntilChanged(),
    filter((status) => [mintStatusMap.minted, mintStatusMap.revoking].includes(status))
  )
  .subscribe(() => loadSoulPassInfo$.next());

// 当用户处于minting状态是定时10s重新获取一次用户minting状态
userWeb3WalletInfo$
  .pipe(
    switchMap((o) => {
      if (o.status === mintStatusMap.minting) {
        return interval(10000).pipe(mapTo(true));
      }
      return of(false);
    }),
    filter(Boolean)
  )
  .subscribe(() => loadUserWeb3WalletInfo$.next());

// wallets$: 兼容 testnet 环境（testnet 环境所有资产均不隐藏）
combineLatest({hideWallets: invalidWallets$, wallets: wallets$})
  .pipe(
    map(({hideWallets, wallets}) => {
      return [...hideWallets, ...wallets].find((o) => o.currencyCode === 369);
    }),
    filter(Boolean)
  )
  .subscribe(phemexEquityWallet$);

async function getSoulPassStatus() {
  const {data, error} = await web3Api.getSoulPassStatus();
  if (error) {
    mc.send('toast', 'error', error);
  }

  return data || defaultUserWeb3WalletInfo;
}

async function getSoulPassOverview() {
  const {data, error} = await web3Api.getSoulPassOverview();
  if (error) {
    mc.send('toast', 'error', error);
  }

  return data || defaultSoulPassInfo;
}
